/**
 * IP GEOLOCATION SERVICE
 *
 * Detects user's country from their IP address using our server-side API proxy.
 * The server calls external geolocation APIs, avoiding browser CSP violations.
 * Used to SUGGEST (not auto-select) country in registration/settings.
 */

export interface GeoLocationResult {
 countryCode: string; // ISO 3166-1 alpha-2 (e.g., 'ZA')
 countryCode3: string; // ISO 3166-1 alpha-3 (e.g., 'ZAF')
 countryName: string;
 city?: string;
 region?: string;
 ip?: string;
 source: string; // Which API provided the data
}

// Cache to avoid repeated API calls
let geoCache: GeoLocationResult | null = null;
let geoCacheTimestamp: number = 0;
const GEO_CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

/**
 * Detect user's country from IP address
 * Calls our server-side proxy at /api/user/geolocation to avoid CSP issues
 */
export async function detectUserCountry(): Promise<GeoLocationResult | null> {
 // Check cache first
 const now = Date.now();
 if (geoCache && (now - geoCacheTimestamp) < GEO_CACHE_DURATION) {
 console.log('Using cached geolocation:', geoCache.countryName);
 return geoCache;
 }

 try {
 const response = await fetch('/api/user/geolocation');
 if (!response.ok) {
 console.warn('Geolocation API returned status:', response.status);
 return null;
 }

 const result = await response.json();
 if (result.success && result.data) {
 geoCache = result.data;
 geoCacheTimestamp = now;
 console.log(`Geolocation detected: ${result.data.countryName} (${result.data.countryCode3}) via ${result.data.source}`);
 return result.data;
 }

 console.warn('Geolocation API returned no data:', result.error);
 return null;
 } catch (error) {
 console.warn('Geolocation detection failed:', error);
 return null;
 }
}

/**
 * Clear geolocation cache
 */
export function clearGeoCache(): void {
 geoCache = null;
 geoCacheTimestamp = 0;
}

